import { getSoilSampleDatasetApi, getSoilSampleDatasetsApi } from 'api-validations/locutus'
import { useOrgCodeOrThrow } from 'shared-ui/active-operation'
import { usePanoramaQuery } from 'shared-ui/api-provider'

import type { SoilSampleLayer } from '../map-state-store'

/**
 * A field's soil sampling datasets, without their points — enough to build the
 * layer list, since every dataset carries the analytes it reports.
 *
 * Newest season first, then shallowest depth, which is the order a grower reads
 * them in: this season's results are the ones being acted on, and the tillage
 * layer is the one most decisions are made from.
 */
export const useSoilSampleDatasets = (planningFieldId: string | undefined) => {
  const orgCode = useOrgCodeOrThrow()

  return usePanoramaQuery(
    getSoilSampleDatasetsApi,
    { orgCode, fieldId: planningFieldId ?? '' },
    {
      enabled: planningFieldId !== undefined,
      select: ({ items }) =>
        items.toSorted((a, b) => {
          if (a.season !== b.season) return b.season - a.season
          return (a.depth?.top ?? 0) - (b.depth?.top ?? 0)
        })
    }
  )
}

/**
 * One dataset with its sample points, for the selected layer.
 *
 * Every analyte in a dataset is measured at the same points, so switching
 * between analytes of one dataset reads from cache rather than refetching.
 */
export const useSoilSampleDataset = (layer: SoilSampleLayer | null) => {
  const orgCode = useOrgCodeOrThrow()

  return usePanoramaQuery(
    getSoilSampleDatasetApi,
    {
      orgCode,
      fieldId: layer?.planningFieldId ?? '',
      datasetId: layer?.samplingEventId ?? ''
    },
    { enabled: layer !== null }
  )
}
